import { useState, type ReactNode } from 'react';
import { api } from '../lib/api.js';
import { FolderPicker } from './FolderPicker.js';

export interface MoveResult {
  moved: number;
  denied: number;
  unchanged: number;
  notFound: number;
  deniedExamples: string[];
  /** Where they went, '' for the top level. */
  folderId: string;
}

/**
 * Choosing where a selection goes.
 *
 * The same click-through picker as the upload page, so finding a folder works
 * the same way whether you are putting new files in it or old ones. Creating a
 * folder from here steps into it, because "make a folder for these" is the
 * usual reason to open this at all.
 */
export function MoveDialog({
  assetIds,
  startFolderId,
  canCreate,
  onClose,
  onMoved,
}: {
  assetIds: string[];
  /** Where the picker opens -- the folder being viewed, or '' for the top. */
  startFolderId: string;
  canCreate: boolean;
  onClose: () => void;
  onMoved: (result: MoveResult) => void;
}): ReactNode {
  const [destination, setDestination] = useState(startFolderId);
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const count = assetIds.length;
  const plural = count === 1 ? '' : 's';

  const move = async (): Promise<void> => {
    setMoving(true);
    setError(null);
    try {
      const result = await api.bulkMoveAssets(assetIds, destination || null);
      onMoved({ ...result, folderId: destination });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not move those files');
      setMoving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 50,
      }}
      // A stray click outside must not discard a destination three levels deep
      // that took a minute to find, so it only closes when nothing is running.
      onClick={() => {
        if (!moving) onClose();
      }}
    >
      <div
        className="panel"
        role="dialog"
        aria-label={`Move ${count} file${plural}`}
        style={{ maxWidth: 560, width: '92%', margin: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ marginTop: 0 }}>
          Move {count.toLocaleString()} file{plural}
        </h3>

        <FolderPicker
          value={destination}
          onChange={setDestination}
          canCreate={canCreate}
          action="Moving to"
        />

        {error && <div className="notice error">{error}</div>}

        <div className="flex" style={{ marginTop: 14 }}>
          <div className="spacer" />
          <button className="btn" onClick={onClose} disabled={moving}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => void move()} disabled={moving}>
            {moving ? 'Moving…' : 'Move here'}
          </button>
        </div>
      </div>
    </div>
  );
}
